import type { FactoriesFactoryPullRequest, FactoriesWorkOrderArtifact } from "@/api-client";

import { factoryAppConfigurePath, factoryAppSplitRunPath } from "../../lib/factoryPagePaths";
import {
  composeIntentDocument,
  EMPTY_INTENT_DOCUMENT,
  INTENT_ARTIFACT_NAME,
  SPEC_ARTIFACT_NAME,
  type IntentDocument,
  parseIntentDocument,
} from "../../lib/intentDocument";
import {
  extractArtifactMarkdownBody,
  extractArtifactName,
  extractArtifactTitle,
  toArtifactDataRecord,
} from "../../lib/workOrderArtifact";
import { getWorkOrderRunHref } from "../../lib/workOrderExecutions";
import { SPLIT_RUN_CLOSURE_PHASE_ID, type SplitRunFixture, type SplitRunPhase } from "./splitRunMocks";
import { isOriginTicketArtifact, type SplitRunSource } from "./splitRunSource";

export type SplitRunPopupTab = "description" | "log";

/**
 * The classic tabbed popup predates the console. Drop the console-only
 * phases (runs from earlier dispatches and the closure card) so orgs
 * without the Task Console feature see the original timeline.
 */
export function classicSplitRunFixture(fixture: SplitRunFixture): SplitRunFixture {
  const phases = fixture.phases.filter((phase) => !phase.historyRun && phase.id !== SPLIT_RUN_CLOSURE_PHASE_ID);
  return phases.length === fixture.phases.length ? fixture : { ...fixture, phases };
}

export function refinePopupShowsAutomations(args: {
  footerKind: SplitRunFixture["footer"]["kind"];
  sourceOnly?: boolean;
}) {
  if (args.sourceOnly) {
    return true;
  }
  return args.footerKind !== "draft";
}

/** Refine overlay size. Chat-only is 48rem. Plan open is 80rem. */
export const SPLIT_RUN_POPUP_DIALOG_CLASSNAME =
  "has-[[data-refine-chat-solo]]:w-[min(48rem,calc(100vw-5rem))] has-[[data-refine-plan-open]]:w-[min(80rem,calc(100vw-5rem))] transition-[width] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none";

/** Refine chat column. */
export const SPLIT_RUN_CHAT_COLUMN_CLASSNAME = "mx-auto w-full max-w-5xl px-4";

/** Reserve the scrollbar lane on the log and the composer so their right edges stay aligned. */
export const SPLIT_RUN_CHAT_SCROLLBAR_GUTTER_CLASSNAME = "overflow-y-auto [scrollbar-gutter:stable]";

/** Shared height for the chat composer and the spec decision row. */
export const SPLIT_RUN_INTENT_PANE_FOOTER_CLASSNAME =
  "flex shrink-0 items-center border-t border-border bg-background px-5 py-3 min-h-[5.5rem]";

const DESCRIPTION_NAMES = ["details.md", "description.md"];

export function isWorkOrderDescriptionName(name: string): boolean {
  return DESCRIPTION_NAMES.includes(name);
}
const PLAN_NAMES = ["plan.md"];

export function defaultSplitRunPopupTab(fixture: SplitRunFixture): SplitRunPopupTab {
  const hasRunningLineStep = fixture.phases.some((phase) => phase.stepIndex != null && phase.status === "running");
  return hasRunningLineStep || hasActivePullRequestActivity(fixture) ? "log" : "description";
}

export function hasActivePullRequestActivity(fixture: SplitRunFixture): boolean {
  return fixture.phases.some(
    (phase) =>
      Boolean(phase.pullRequestActivity) &&
      (phase.status === "running" || phase.status === "pending" || phase.status === "waiting"),
  );
}

function phaseRun(phase: SplitRunPhase | undefined): { appId: string; runId: string } | undefined {
  const appId = phase?.appId;
  const runId = phase?.runId;
  if (!appId || !runId) {
    return undefined;
  }
  return { appId, runId };
}

/**
 * Open the automation run for the selected log phase. If that phase has
 * no run, use the latest phase run or the footer run.
 */
export function splitRunAutomationRunHref(args: {
  organizationId?: string;
  factoryKey?: string;
  orderNumber?: string;
  fixture: SplitRunFixture;
  preferredPhaseId?: string | null;
}): string | null {
  const { organizationId, factoryKey, orderNumber, fixture, preferredPhaseId } = args;
  if (!organizationId || !factoryKey) {
    return null;
  }
  const preferred = phaseRun(fixture.phases.find((phase) => phase.id === preferredPhaseId));
  const current = phaseRun(fixture.phases.find((phase) => phase.id === fixture.currentPhaseId));
  const latest = fixture.phases.reduceRight<{ appId: string; runId: string } | undefined>(
    (found, phase) => found ?? phaseRun(phase),
    undefined,
  );
  const run = preferred ?? current ?? latest ?? fixture.footer.run;
  return getWorkOrderRunHref(organizationId, factoryKey, run?.appId, run?.runId, { orderNumber });
}

/** Configure URL of the automation that owns a log phase. */
export function splitRunPhaseAutomationHref(args: {
  organizationId?: string;
  factoryKey?: string;
  orderNumber?: string;
  phase: SplitRunPhase;
}): string | undefined {
  const { organizationId, factoryKey, orderNumber, phase } = args;
  if (!organizationId || !factoryKey || !phase.appId) {
    return undefined;
  }
  return factoryAppConfigurePath(organizationId, factoryKey, phase.appId, { orderNumber });
}

/** Full-screen run page with the log in the sidebar. */
export function splitRunPhaseRunHref(args: {
  organizationId?: string;
  factoryKey?: string;
  orderNumber?: string;
  lineId?: string;
  phase: SplitRunPhase;
}): string | undefined {
  const { organizationId, factoryKey, orderNumber, lineId, phase } = args;
  if (!organizationId || !factoryKey || !phase.appId) {
    return undefined;
  }
  return factoryAppSplitRunPath(organizationId, factoryKey, phase.appId, {
    from: "task",
    orderNumber,
    lineId,
    runId: phase.runId,
    canvas: phase.canvasKey ?? undefined,
  });
}

export function resolveSplitRunPopupArtifacts(args: {
  fixtureArtifacts: FactoriesWorkOrderArtifact[];
  liveArtifacts?: FactoriesWorkOrderArtifact[];
  useLive: boolean;
}): FactoriesWorkOrderArtifact[] {
  if (args.useLive) {
    return args.liveArtifacts ?? [];
  }
  return args.fixtureArtifacts;
}

export function resolveSplitRunPopupPullRequests(args: {
  fixturePullRequests: FactoriesFactoryPullRequest[];
  livePullRequests?: FactoriesFactoryPullRequest[];
  useLive: boolean;
}): FactoriesFactoryPullRequest[] {
  if (args.useLive) {
    return args.livePullRequests ?? [];
  }
  return args.fixturePullRequests;
}

export function collectSplitRunArtifacts(fixture: SplitRunFixture): FactoriesWorkOrderArtifact[] {
  const seen = new Set<string>();
  const artifacts: FactoriesWorkOrderArtifact[] = [];
  for (const phase of fixture.phases) {
    for (const artifact of phase.artifacts) {
      const key = artifact.id ?? `${artifact.type}-${artifactName(artifact)}-${artifact.createdAt}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      artifacts.push(artifact);
    }
  }
  return artifacts;
}

/** Unique pull requests from every phase stream, first occurrence wins. */
export function collectSplitRunPullRequests(fixture: SplitRunFixture): FactoriesFactoryPullRequest[] {
  const seen = new Set<string>();
  const pullRequests: FactoriesFactoryPullRequest[] = [];
  for (const phase of fixture.phases) {
    for (const line of phase.stream) {
      const pullRequest = line.pullRequest;
      if (!pullRequest) {
        continue;
      }
      const key = pullRequest.id ?? pullRequest.url ?? String(pullRequest.number ?? "");
      if (!key || seen.has(key)) {
        continue;
      }
      seen.add(key);
      pullRequests.push(pullRequest);
    }
  }
  return pullRequests;
}

export function splitRunDescriptionMarkdown(artifacts: FactoriesWorkOrderArtifact[]): string {
  return firstArtifactMarkdown(artifacts, DESCRIPTION_NAMES);
}

export function splitRunIntentMarkdown(artifacts: FactoriesWorkOrderArtifact[]): string {
  return firstArtifactMarkdown(artifacts, [SPEC_ARTIFACT_NAME, INTENT_ARTIFACT_NAME]);
}

export function splitRunIntentDocument(args: {
  artifacts: FactoriesWorkOrderArtifact[];
  description: string;
  skipDescriptionFallback?: boolean;
}): IntentDocument {
  const intent = splitRunIntentMarkdown(args.artifacts);
  if (intent) {
    return parseIntentDocument(intent);
  }
  if (args.skipDescriptionFallback) {
    return EMPTY_INTENT_DOCUMENT;
  }
  return composeIntentDocument(args.description, firstArtifactMarkdown(args.artifacts, PLAN_NAMES));
}

/** Live saves win. Storybook still prefers artifact markdown. */
export function splitRunSourceDescription(args: {
  workOrderDescription?: string;
  artifactDescription?: string;
  preferWorkOrder?: boolean;
}): string {
  const workOrder = args.workOrderDescription?.trim() ?? "";
  const artifact = args.artifactDescription?.trim() ?? "";
  if (args.preferWorkOrder) {
    return workOrder || artifact;
  }
  return artifact || workOrder;
}

/**
 * Every artifact on the task for the console summary panel, including
 * document markdowns such as description.md and spec.md. Only the origin
 * ticket stays out, because the Source row already links it. Oldest first.
 */
export function splitRunPanelArtifacts(
  artifacts: FactoriesWorkOrderArtifact[],
  source?: SplitRunSource,
): FactoriesWorkOrderArtifact[] {
  return artifacts.filter((artifact) => !isOriginTicketArtifact(artifact, source)).sort(compareArtifactsByCreatedAt);
}

/** Files and links that are not already the description body. Oldest first. */
export function splitRunLinkedArtifacts(
  artifacts: FactoriesWorkOrderArtifact[],
  source?: SplitRunSource,
): FactoriesWorkOrderArtifact[] {
  return artifacts
    .filter((artifact) => {
      if (
        DESCRIPTION_NAMES.includes(artifactName(artifact)) ||
        artifactName(artifact) === INTENT_ARTIFACT_NAME ||
        artifactName(artifact) === SPEC_ARTIFACT_NAME
      ) {
        return false;
      }
      return !isOriginTicketArtifact(artifact, source);
    })
    .sort(compareArtifactsByCreatedAt);
}

function compareArtifactsByCreatedAt(left: FactoriesWorkOrderArtifact, right: FactoriesWorkOrderArtifact): number {
  return artifactCreatedAtMs(left) - artifactCreatedAtMs(right);
}

function artifactCreatedAtMs(artifact: FactoriesWorkOrderArtifact): number {
  const parsed = Date.parse(artifact.createdAt ?? "");
  // MAX_SAFE_INTEGER, not Infinity: undated artifacts sort last, and the
  // comparator stays finite so the stable sort keeps their input order.
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function firstArtifactMarkdown(artifacts: FactoriesWorkOrderArtifact[], names: readonly string[]): string {
  for (const name of names) {
    const artifact = artifacts.find((entry) => artifactName(entry) === name);
    const body = extractArtifactMarkdownBody(toArtifactDataRecord(artifact?.data))?.trim();
    if (body) {
      return body;
    }
  }
  return "";
}

function artifactName(artifact: FactoriesWorkOrderArtifact): string {
  const data = toArtifactDataRecord(artifact.data);
  return extractArtifactName(data)?.trim() || extractArtifactTitle(data)?.trim() || "";
}
